import { Plus } from '@phosphor-icons/react';
import {
  Button,
  Inline,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Toggle,
} from '@reelplicate/ui';
import { type ReactNode, useState } from 'react';
import { Link as RouterLink } from 'react-router';

import type { StyleOption } from './caption-style-options';
import { StyleCard } from './style-card';

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'custom', label: 'Your styles' },
] as const;

/**
 * Figma "Style picker" (396:2288): pick a caption style for the carousel, or
 * for one slide - which can also go back to the carousel's style. Picking
 * applies straight away.
 */
export function StylePicker({
  trigger,
  title,
  options,
  value,
  imageUrl,
  followsCarousel,
  onFollowCarousel,
  onSelect,
  onNewStyle,
}: {
  trigger: ReactNode;
  title: string;
  options: StyleOption[];
  /** The ref picked; unset when a slide follows the carousel. */
  value?: string;
  imageUrl?: string | null;
  /** Slides only: whether it uses the carousel's style. */
  followsCarousel?: boolean;
  onFollowCarousel?: (follow: boolean) => void;
  onSelect: (ref: string) => void;
  onNewStyle: () => void;
}) {
  const [open, setOpen] = useState(false);
  const pick = (ref: string) => {
    onSelect(ref);
    setOpen(false);
  };
  const grid = (items: StyleOption[]) =>
    items.length ? (
      <div className="grid grid-cols-3 gap-3">
        {items.map((option) => (
          <StyleCard
            key={option.ref}
            option={option}
            imageUrl={imageUrl}
            selected={!followsCarousel && option.ref === value}
            onSelect={() => pick(option.ref)}
          />
        ))}
      </div>
    ) : (
      <p className="text-body-sm text-text-secondary">
        No styles of your own yet. Make one with New style.
      </p>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        size="lg"
        side="left"
        align="start"
        title={title}
        className="w-132"
        footer={
          <Inline gap={3} alignY="center" className="w-full">
            <Button
              type="button"
              size="sm"
              variant="outline"
              leadingIcon={Plus}
              onClick={() => {
                setOpen(false);
                onNewStyle();
              }}
            >
              New style
            </Button>
            <RouterLink
              to="/settings/brand-profile"
              className="text-label-sm text-text-link hover:underline"
            >
              Manage in Brand profile
            </RouterLink>
          </Inline>
        }
      >
        <Stack gap={4} className="max-h-[60vh] overflow-y-auto">
          {onFollowCarousel && (
            <div className="bg-bg-subtle flex items-center gap-3 rounded-md px-3 py-2.5">
              <Stack gap={0} className="flex-1">
                <span className="text-body-sm-strong text-text-primary">
                  Use the carousel style
                </span>
                <span className="text-caption text-text-secondary">
                  Off: this slide keeps a style of its own.
                </span>
              </Stack>
              <Toggle
                aria-label="Use the carousel style"
                checked={followsCarousel}
                onCheckedChange={(follow) => {
                  onFollowCarousel(follow);
                  // Back to the carousel's style: nothing more to pick.
                  if (follow) setOpen(false);
                }}
              />
            </div>
          )}
          <Tabs defaultValue="all">
            <TabList variant="pill">
              {TABS.map((tab) => (
                <Tab key={tab.value} value={tab.value}>
                  {tab.label}
                </Tab>
              ))}
            </TabList>
            {TABS.map((tab) => (
              <TabPanel key={tab.value} value={tab.value} className="pt-3">
                {grid(
                  tab.value === 'all'
                    ? options
                    : options.filter((o) => o.source === tab.value),
                )}
              </TabPanel>
            ))}
          </Tabs>
        </Stack>
      </PopoverContent>
    </Popover>
  );
}
